import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.polyline.snakeanim';
import 'leaflet-polylinedecorator';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-textpath';
import markerSvg from '../assets/icons/marker.svg';
import grayPlaceSvg from '../assets/icons/gray-place.svg';
import redPlaceSvg from '../assets/icons/red-place.svg';

import { useFilters } from '../utils/FilterContext';
import { useSearch } from '../utils/SearchContext';
import {useRoute} from "../utils/RouteContext";

// Импорт компонентов модального окна и его контента
import ActionSheet from '../components/ui/modals/ActionSheet';
import PlaceCardComponent from '../components/ui/modalsContent/PlaceCardComponent';
import ModalPortal from "../ModalPortal";
import RouteComponent from "../components/ui/modalsContent/RouteComponent";



const customClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    const svg = `
    <svg width="68" height="41" viewBox="0 0 68 41" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="68" height="41" rx="15" fill="#FFFFFF" fill-opacity="0.85"/>
      <path
     fill-rule="evenodd"
     clip-rule="evenodd"
     d="m 50.378384,8.6422534 c -4.974238,0 -9.006651,4.5062306 -9.006651,9.5695676 0,5.023684 2.874608,10.484192 7.359672,12.580488 1.045447,0.488725 2.248511,0.488725 3.293958,0 4.485087,-2.096296 7.359672,-7.556804 7.359672,-12.580488 0,-5.063337 -4.03239,-9.5695676 -9.006651,-9.5695676 z m 0,11.2583146 c 1.243593,0 2.251663,-1.00807 2.251663,-2.251663 0,-1.24356 -1.00807,-2.251663 -2.251663,-2.251663 -1.243593,0 -2.251663,1.008103 -2.251663,2.251663 0,1.243593 1.00807,2.251663 2.251663,2.251663 z"
     fill="#000000"
     id="path1-1"
     style="stroke-width:1.12583;fill:#1e2224;fill-opacity:1" />
     <text x="21.31801"
     y="28.879999"
     id="count-container"
     style="font-style:normal;font-variant:normal;font-weight:bold;font-stretch:normal;font-size:24px;line-height:0.8;font-family:Montserrat;-inkscape-font-specification:'Montserrat, Bold';font-variant-ligatures:normal;font-variant-caps:normal;font-variant-numeric:normal;font-variant-east-asian:normal;text-align:center;text-anchor:middle;fill:#1e2224;fill-opacity:1;stroke:none;stroke-width:2"><tspan
       sodipodi:role="line"
       id="counter"
       x="21.31801"
       y="28.879999">
        <tspan id="counter">${count}</tspan>
      </text>
    </svg>
  `;
    return L.divIcon({
        html: svg,
        className: 'custom-cluster-icon',
        iconSize: [68, 41],
        iconUrl: markerSvg
    });
};

function ViewRoadNetwork({ roadsData }) {
    const map = useMap();
    const { activeFilters, CATEGORIES } = useFilters();
    const { selectedMarker, setSelectedMarker } = useSearch();
    const { routeData} = useRoute();

    const { pathname } = useLocation();
    const isPanorama = pathname === '/panorama';

    const routeLayerRef = useRef(null);
    const [showActionSheet, setShowActionSheet] = useState(false);
    const [showRouteSheet, setShowRouteSheet] = useState(false); // Новое состояние

    // 1. Локальный стейт для «пропсов»,
    //    которые мы хотим передать в RouteComponent, когда откроем его
    const [routeComponentProps, setRouteComponentProps] = useState({});

    const [selectedMarkerData, setSelectedMarkerData] = useState(null);
    const selectedMarkerRef = useRef(null);
    const geojsonLayerRef = useRef(null);
    const markerClusterRef = useRef(null); // Один кластер вместо множества
    const categoryLayersRef = useRef({}); // Слои для каждой категории
    const prevMarkerElRef = useRef(null);

    // Создаём DivIcon для обычного маркера createNormalDivIcon
    // Создаём DivIcon для маркера с учетом категории
    const createNormalDivIcon = (category) => {

        // Нормализуем категорию для использования в классе
        const normalizedCategory = (category || 'other').toLowerCase();

        return L.divIcon({
            html: '<div class="my-marker-icon marker-category-' + normalizedCategory + '"></div>',
            className: '',
            iconSize: [41, 41],
            iconAnchor: [20, 41],
        });
    };

    const createStartMarkerIcon = () => {
        return L.icon({
            iconUrl: grayPlaceSvg,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32]
        });
    };

    const createEndMarkerIcon = () => {
        return L.icon({
            iconUrl: redPlaceSvg,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32]
        });
    };

    // При начале закрытия модалки
    const handleCloseModalStart = () => {
        if (prevMarkerElRef.current) {
            prevMarkerElRef.current.classList.remove('highlighted');
            prevMarkerElRef.current = null;
        }
    };

    // По окончании анимации закрытия
    const handleCloseModalEnd = () => {
        setShowActionSheet(false);

        if (selectedMarkerRef.current) {
            // Сохраняем категорию объекта
            const category = selectedMarkerRef.current.feature.properties.category || 'Other';

            // Удаляем маркер с карты
            selectedMarkerRef.current.remove();

            // Добавляем обратно в кластер только если его категория активна
            if (activeFilters[category]) {
                if (markerClusterRef.current && !markerClusterRef.current.hasLayer(selectedMarkerRef.current)) {
                    markerClusterRef.current.addLayer(selectedMarkerRef.current);
                }
            }
            // Сбрасываем ссылку на выбранный маркер
            selectedMarkerRef.current = null;
        }

        // Сбрасываем ссылку на элемент маркера
        prevMarkerElRef.current = null;
    };



    // Функция для обновления подписей дорог
    const toggleRoadLabels = useCallback(() => {
        if (!geojsonLayerRef.current) return;
        const currentZoom = map.getZoom();

        geojsonLayerRef.current.eachLayer((layer) => {
            if (typeof layer.setText === 'function') {
                if (layer._textNode) {
                    layer._textNode.remove();
                    layer._textNode = null;
                }
                const roadName = layer.feature?.properties?.name || '';
                if (currentZoom >= 17 && roadName) {
                    layer.setText(roadName, {
                        repeat: false,
                        center: true,
                        offset: 0,
                        attributes: {
                            fill: 'black',
                            'font-size': '14px',
                            'font-weight': 'bold',
                            'font-family': 'Montserrat',
                            opacity: '0.8',
                        },
                    });
                } else {
                    layer.setText(null);
                }
            }
        });
    }, [map]);

    // Эффект для применения фильтров
    useEffect(() => {
        if (!categoryLayersRef.current || !markerClusterRef.current) return;

        // Показать/скрыть маркеры в зависимости от активных фильтров
        Object.entries(categoryLayersRef.current).forEach(([category, markers]) => {
            markers.forEach(marker => {
                if (activeFilters[category]) {
                    // Если фильтр активен, добавляем маркер в кластер
                    if (!markerClusterRef.current.hasLayer(marker)) {
                        markerClusterRef.current.addLayer(marker);
                    }
                } else {
                    // Если фильтр не активен, убираем маркер из кластера
                    if (markerClusterRef.current.hasLayer(marker)) {
                        markerClusterRef.current.removeLayer(marker);
                    }
                }
            });
        });
    }, [activeFilters]);

    // Инициализация/обновление слоёв
    useEffect(() => {
        if (!roadsData) return;

        // Очищаем предыдущие слои
        if (geojsonLayerRef.current) {
            geojsonLayerRef.current.remove();
        }

        if (markerClusterRef.current) {
            map.removeLayer(markerClusterRef.current);
        }

        // Сбрасываем слои категорий
        categoryLayersRef.current = {};

        // Создаём слой с дорогами
        geojsonLayerRef.current = L.geoJSON(roadsData, {
            filter: (feature) => feature.geometry.type !== 'Point',
            style: () => ({
                color: 'transparent',
                weight: 4,
                interactive: false,
            }),
        }).addTo(map);

        // Создаем один кластер для всех маркеров
        markerClusterRef.current = L.markerClusterGroup({
            maxClusterRadius: 50,
            updateWhenIdle: false,
            removeOutsideVisibleBounds: false,
            iconCreateFunction: customClusterIcon,
        });

        // Инициализируем массивы для хранения маркеров по категориям
        CATEGORIES.forEach(category => {
            categoryLayersRef.current[category] = [];
        });

        // Добавляем маркеры в соответствующие слои
        const pointFeatures = roadsData.features.filter(feature =>
            feature.geometry.type === 'Point'
        );

        L.geoJSON({
            type: "FeatureCollection",
            features: pointFeatures
        }, {
            onEachFeature: (feature, layer) => {
                // Добавляем координаты в свойства фичи (GeoJSON: [lng, lat])
                feature.properties.latitude = feature.geometry.coordinates[1];
                feature.properties.longitude = feature.geometry.coordinates[0];
                // Получаем категорию маркера или используем "Other" по умолчанию
                const category = feature.properties.category || 'other';

                // Добавляем маркер в соответствующий массив по категории
                if (categoryLayersRef.current[category]) {
                    categoryLayersRef.current[category].push(layer);
                } else {
                    categoryLayersRef.current['other'].push(layer);
                }

                if (isPanorama && category === 'panorama') {
                    // в режиме panorama — сразу навешиваем один обработчик
                    layer.on('click', () => {
                        const name = feature.properties.name;
                        const url = `/panoramaView.html?image=${encodeURIComponent(name)}`;
                        window.open(url, '_blank');
                    });
                }
                else if (!isPanorama) {
                    // в обычном режиме — сразу навешиваем всё нужное
                    layer.on('click', (e) => {
                        // снять highlight с прошлой метки
                        if (prevMarkerElRef.current) {
                            prevMarkerElRef.current.classList.remove('highlighted');
                        }

                        // вернуть прошлый маркер в кластер
                        if (selectedMarkerRef.current && selectedMarkerRef.current !== layer) {
                            selectedMarkerRef.current.remove();
                            if (markerClusterRef.current && !markerClusterRef.current.hasLayer(selectedMarkerRef.current)) {
                                markerClusterRef.current.addLayer(selectedMarkerRef.current);
                            }
                        }

                        // удалить текущий маркер из кластера и добавить на карту
                        if (markerClusterRef.current.hasLayer(layer)) {
                            markerClusterRef.current.removeLayer(layer);
                            layer.addTo(map);
                        }

                        selectedMarkerRef.current = layer;
                        setSelectedMarkerData(feature.properties);
                        setShowActionSheet(true);

                        // подсветить новый маркер
                        setTimeout(() => {
                            const markerEl = layer.getElement?.()?.querySelector('.my-marker-icon');
                            if (markerEl) {
                                markerEl.classList.add('highlighted');
                                prevMarkerElRef.current = markerEl;
                            }
                        }, 0);

                        // паним карту чуть выше метки
                        const zoom = map.getZoom();
                        const pt = map.project(e.latlng, zoom);
                        pt.y += 70;
                        map.panTo(map.unproject(pt, zoom));
                    });
                }
            },
            pointToLayer: (feature, latlng) => {
                // Получаем категорию из свойств feature
                const category = feature.properties.category;

                // Передаем категорию в функцию создания иконки
                return L.marker(latlng, { icon: createNormalDivIcon(category) });
            }
        });

        // Наполняем кластер: в панораме — только категория "panorama", иначе — все категории кроме "panorama"
        Object.entries(categoryLayersRef.current).forEach(([category, markers]) => {
// Показываем маркеры только тех категорий, что включены в activeFilters
// и при этом:
// — в режиме panorama только category === 'panorama'
// — в обычном режиме все, кроме 'panorama'
            const shouldShow =
                activeFilters[category] &&
                (
                    (isPanorama && category === 'panorama') ||
                    (!isPanorama && category !== 'panorama')
                );

            if (shouldShow) {
                markers.forEach(marker => {
                    markerClusterRef.current.addLayer(marker);
                });
            }

        });


        // Добавляем кластер на карту
        map.addLayer(markerClusterRef.current);

        // Обновляем подписи дорог
        toggleRoadLabels();

        return () => {
            if (geojsonLayerRef.current) {
                geojsonLayerRef.current.remove();
            }
            if (markerClusterRef.current) {
                map.removeLayer(markerClusterRef.current);
            }
        };
    }, [roadsData, map, CATEGORIES, activeFilters, toggleRoadLabels]); // Добавлен toggleRoadLabels

    // Обновление подписей дорог при изменении зума
    useEffect(() => {
        map.on('zoomend', toggleRoadLabels);
        return () => {
            map.off('zoomend', toggleRoadLabels);
        };
    }, [map, toggleRoadLabels]);

    // Добавьте этот useEffect для обработки выбранного маркера
    useEffect(() => {
        if (!selectedMarker || !markerClusterRef.current || !map) return;

        try {
            if (selectedMarker.geometry &&
                selectedMarker.geometry.coordinates &&
                selectedMarker.geometry.coordinates.length >= 2) {

                const [lng, lat] = selectedMarker.geometry.coordinates;

                if (typeof lat === 'number' && typeof lng === 'number') {
                    const category = selectedMarker.properties.category || 'Other';

                    // Вместо изменения фильтров, просто добавим нужный маркер на карту
                    map.setView([lat, lng], 17);

                    // Найдем нужный маркер в массиве категорий, даже если категория скрыта
                    if (categoryLayersRef.current[category]) {
                        const matchingMarker = categoryLayersRef.current[category].find(marker =>
                            marker.feature &&
                            marker.feature.properties.name === selectedMarker.properties.name
                        );

                        if (matchingMarker) {
                            // Добавляем маркер напрямую на карту (не в кластер)
                            matchingMarker.addTo(map);

                            selectedMarkerRef.current = matchingMarker;
                            setSelectedMarkerData(matchingMarker.feature.properties);
                            setShowActionSheet(true);

                            // Добавляем подсветку
                            setTimeout(() => {
                                const markerEl = matchingMarker.getElement?.()?.querySelector('.my-marker-icon');
                                if (markerEl) {
                                    markerEl.classList.add('highlighted');
                                    prevMarkerElRef.current = markerEl;
                                }
                            }, 100);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Ошибка при обработке выбранного маркера:", error);
        } finally {
            setSelectedMarker(null);
        }
    }, [selectedMarker, map, setSelectedMarker]); // Добавлен setSelectedMarker

    // Отрисовываем маршрут, если routeData не null
    useEffect(() => {
        // Сносим старый слой, если есть
        if (routeLayerRef.current) {
            routeLayerRef.current.remove();
            routeLayerRef.current = null;
        }

        if (routeData) {
            routeLayerRef.current = L.layerGroup().addTo(map);
            const coords = routeData.geometry.coordinates.map(c => [c[1], c[0]]);
            L.marker(coords[0], {
                icon: createStartMarkerIcon()
            }).addTo(routeLayerRef.current);

            // (2) Фоновая линия — белая, чуть толще, без dashArray
            const bgLine = L.polyline(coords, {
                color:   '#ffffff',
                weight:  16,        // фон выше основного веса (например +4)
                opacity: 1,
                noClip:                true,
                updateWhileAnimating:  true,
                updateWhileInteracting:true,
                // если хотите, чтобы фон тоже «змейкой» появлялся:
                snakingSpeed: 200,
                animate:      true,
            }).addTo(routeLayerRef.current);

            bgLine.snakeIn();  // или убрать, если фон сразу должен отображаться

            // (3) Основная линия — пунктирная, анимированная
            const routeLine = L.polyline(coords, {
                color:      '#BA3838',
                weight:     10,
                opacity:    1,
                dashArray:  '1,15',
                className:  'animated-dashed',
                snakingSpeed:          200,
                animate:               true,
                noClip:                true,
                updateWhileAnimating:  true,
                updateWhileInteracting:true,
            }).addTo(routeLayerRef.current);

            routeLine.snakeIn();

            // 3. По завершении snakeIn добавляем конечный маркер
            routeLine.on('snakeend', () => {
                L.marker(coords[coords.length - 1], { icon: createEndMarkerIcon() })
                    .addTo(routeLayerRef.current);
            });


            // 6. Подгоняем видимость области
            map.fitBounds(L.latLngBounds(coords), { padding: [50,50] });
        }
    }, [routeData, map]);

    const handleCloseRouteModalEnd = () => {
        setShowRouteSheet(false);
    };

    // 2. useEffect для прослушки события 'openRouteModal'
    useEffect(() => {
        // Обязательно передаём (e) => {}
        // вместо использования глобального `event`
        const openRouteModalHandler = (e) => {
            const { forceResetOnMount } = e.detail || {};
            console.log("openRouteModalHandler, detail:", e.detail);

            // Записываем в стейт routeComponentProps
            setRouteComponentProps({ forceResetOnMount });

            // Показываем RouteComponent (например, через showRouteSheet = true)
            setShowRouteSheet(true);
        };

        document.addEventListener('openRouteModal', openRouteModalHandler);

        // Чистим слушатель при размонтировании
        return () => {
            document.removeEventListener('openRouteModal', openRouteModalHandler);
        };
    }, []);



    return (
        <>
            {showActionSheet && (
                <ModalPortal>
                    <ActionSheet
                        onCloseStart={handleCloseModalStart}
                        onCloseEnd={handleCloseModalEnd}
                    >
                        {(closeSheetFn) => (
                            <PlaceCardComponent
                                data={selectedMarkerData}
                                onClose={closeSheetFn} // <-- передаём её в PlaceCardComponent
                            />
                        )}
                    </ActionSheet>
                </ModalPortal>
            )}
            {showRouteSheet && (
                <ModalPortal>
                    <ActionSheet
                        onCloseStart={() => {}}
                        onCloseEnd={handleCloseRouteModalEnd}
                    >
                        <RouteComponent
                            onClose={() => setShowRouteSheet(false)}
                            roadsData={roadsData}
                            setSelectedMarker={setSelectedMarker}
                            forceResetOnMount={true}
                        />
                    </ActionSheet>
                </ModalPortal>
            )}
        </>
    );
}

export default ViewRoadNetwork;